import React, { useMemo, memo, useCallback } from 'react';
import { Stack, Box, Typography, Button, useTheme, TextField, InputAdornment } from '@mui/material';
import { isEqual } from 'lodash';
import { OBJECT_OF_PLAY } from '@/shared/hooks/useServerState';

import TeamPlayersList from './TeamPlayersList';
import { AutoIsoPlayer } from '@/features/autoIso/types/AutoIsoModel';
import { ProcessedTeam } from '../types/AutoIso';
import { useCurrentGame } from '@/features/games/context/CurrentGameContext';
import { PlayerItem } from './PlayerItem';
import { getTeamLogoUrl } from '@/shared/services/getTeamLogoUrl';
import { getObjectOfPlayLabel } from '@/features/ses/sports/registry';

// Memoized team item component to prevent unnecessary re-renders
const TeamItem = memo(
  ({
    team,
    index,
    trackEntity,
    singleCurrentlyTrackedObjectId,
    sx,
  }: {
    team: ProcessedTeam;
    index: number;
    trackEntity: (ent: any) => void;
    singleCurrentlyTrackedObjectId: number | null;
    sx?: object;
  }) => {
    // Memoize players array to prevent recreation on every render
    const players = useMemo(
      () => Object.values(team.players).flat() as AutoIsoPlayer[],
      [team.players],
    );

    return (
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'center',
          overflow: 'hidden',
          maxHeight: 300,
          overflowY: 'auto',
        }}
      >
     
        <TeamPlayersList
          index={index}
          team={team as any}
          players={players}
          onPlayerClick={(p) => trackEntity(p)}
          singleCurrentlyTrackedObjectId={singleCurrentlyTrackedObjectId}
          sx={{ minWidth: '100px', height: '100%' }}
        />
      </Box>
    );
  },
  (prevProps, nextProps) => {
    // Only re-render if team data or tracked object actually changed
    return (
      prevProps.team.id === nextProps.team.id &&
      prevProps.index === nextProps.index &&
      prevProps.singleCurrentlyTrackedObjectId === nextProps.singleCurrentlyTrackedObjectId &&
      isEqual(prevProps.team.players, nextProps.team.players)
    );
  },
);

export const TrackedObjectSelector: React.FC<{
  sx?: object;
  value?: any;
  setFixedCameraClip: () => void;
  selectTrackedObject: (ent: any, source?: string) => void;
  featuredPlayers?: AutoIsoPlayer[];
}> = ({ sx, featuredPlayers, setFixedCameraClip, selectTrackedObject, value }) => {
  const { fullProcessedTeams, getFeaturedPlayers, game } = useCurrentGame();

  // The object of play is sport-specific ("Puck" for NHL, "Ball" for NBA),
  // resolved from the active game's league.
  const objectOfPlayLabel = useMemo(
    () => getObjectOfPlayLabel(game?.season?.league?.name),
    [game?.season?.league?.name],
  );

  const [puckSelected, setPuckSelected] = React.useState<boolean>(false);
  const [selectedPlayerId, setSelectedPlayerId] = React.useState<number | null>(
    value ? value?.id : null,
  );
  const [fixedCameraClipSelected, setFixedCameraClipSelected] = React.useState<boolean>(false);
  const [searchQuery, setSearchQuery] = React.useState('');

  const handleSelectedCameraClip = useCallback(() => {
    setFixedCameraClip();
    setFixedCameraClipSelected(true);
    setPuckSelected(false);
    setSelectedPlayerId(null);
  }, [setFixedCameraClip]);

  const selectPuckOrBallTracking = useCallback(() => {
    selectTrackedObject(OBJECT_OF_PLAY);
    setSelectedPlayerId(null);
    setPuckSelected(true);
    setFixedCameraClipSelected(false);
  }, [selectTrackedObject]);

  const selectPlayer = useCallback(
    (ent: any) => {
      selectTrackedObject(ent, 'AutoISOTeamSelector');
      setSelectedPlayerId(ent.id);
      setPuckSelected(false);
      setFixedCameraClipSelected(false);
    },
    [selectTrackedObject],
  );

  const theme = useTheme();

  const featuredPlayersMemo = useMemo(() => {
    if (!featuredPlayers || featuredPlayers.length === 0) return [];
    //@ts-expect-error
    return getFeaturedPlayers(featuredPlayers);
  }, [featuredPlayers, getFeaturedPlayers]);

  const teamLogoPath = useCallback((team: any) => getTeamLogoUrl(team), []);
  const teamColor = useCallback(
    (team: any) => team?.colors?.[0]?.hex_color || '#000000',
    [],
  );

  const filteredTeams = useMemo(() => {
    if (!searchQuery.trim() || !fullProcessedTeams) return fullProcessedTeams;

    const query = searchQuery.toLowerCase().trim();

    return fullProcessedTeams.map((team: ProcessedTeam) => {
      const filteredPlayers = Object.entries(team.players).reduce(
        (acc, [position, players]) => {
          const matchingPlayers = (players as AutoIsoPlayer[]).filter(
            (player) =>
              player.displayName.toLowerCase().includes(query) ||
              player.playerNumber?.toLowerCase().includes(query),
          );
          if (matchingPlayers.length > 0) {
            acc[position] = matchingPlayers;
          }
          return acc;
        },
        {} as typeof team.players,
      );

      return { ...team, players: filteredPlayers };
    });
  }, [fullProcessedTeams, searchQuery]);

  return (
    <Stack
      spacing={0}
      sx={{
        height: '100%',
        overflow: 'hidden',
        ...sx,
      }}
    >
      {/* Header */}
      <Stack
        direction="row"
        sx={{ mb: 1, minHeight: '10px', flexShrink: 0, alignItems: 'center', justifyContent: 'space-between' }}
      >
        <Typography
          sx={{
            color: theme.palette.cx.text.primary,
            fontWeight: 500,
            fontSize: '12px',
            lineHeight: '16px',
            letterSpacing: '0px',
          }}
        >
          Make a selection
        </Typography>

        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            sx={{
              color: puckSelected ? theme.palette.cx.bg.surface : theme.palette.cx.text.primary,
              backgroundColor: puckSelected
                ? theme.palette.cx.text.primary
                : theme.palette.cx.bg.surface,
              textTransform: 'none',
              fontWeight: 400,
              fontSize: '10px',
              lineHeight: '14px',
              letterSpacing: '0px',
              borderRadius: 1,
              padding: '2px 8px',
            }}
            variant="text"
            onClick={selectPuckOrBallTracking}
          >
            {objectOfPlayLabel}
          </Button>
          <Button
            sx={{
              color: fixedCameraClipSelected
                ? theme.palette.cx.bg.surface
                : theme.palette.cx.text.primary,
              backgroundColor: fixedCameraClipSelected
                ? theme.palette.cx.text.primary
                : theme.palette.cx.bg.surface,
              textTransform: 'none',
              fontWeight: 400,
              fontSize: '10px',
              lineHeight: '14px',
              letterSpacing: '0px',
              borderRadius: 1,
              padding: '2px 8px',
            }}
            variant="text"
            onClick={handleSelectedCameraClip}
          >
            Fixed Camera Clip
          </Button>
        </Box>
      </Stack>

      {featuredPlayersMemo && featuredPlayersMemo.length > 0 && (
        <Stack direction="column" key="featuredPlayers" sx={{ flex: 1, overflow: 'hidden' }}>
          <Box sx={{ mb: 1, display: 'flex', alignItems: 'left' }}>
            <Typography
              sx={{
                color: theme.palette.cx.text.secondary,
                fontWeight: 400,
                fontSize: '12px',
                lineHeight: '16px',
              }}
            >
              Featured in Event
            </Typography>
          </Box>

          <Stack
            direction="row"
            spacing={1}
            sx={{
              overflowX: 'auto',
              pb: 1,
              width: featuredPlayersMemo.length > 1 ? '100%' : '50%',
            }}
          >
            {featuredPlayersMemo.map((player) => (
              //@ts-expect-error
              <PlayerItem
                key={player.id}
                player={player}
                onPlayerClick={() => selectPlayer(player)}
                teamLogoPath={teamLogoPath(player.team)}
                teamColor={teamColor(player.team)}
                sx={{ cursor: 'pointer' }}
                isTrackedObject={selectedPlayerId === player.id}
              />
            ))}
          </Stack>
        </Stack>
      )}

      <TextField
        variant="outlined"
        size="small"
        placeholder="Search Players..."
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        slotProps={{
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <Box
                  sx={{
                    color: theme.palette.cx.text.secondary,
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 14 14"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M6.12467 2.04159C3.86951 2.04159 2.04134 3.86976 2.04134 6.12492C2.04134 8.38008 3.86951 10.2083 6.12467 10.2083C7.25234 10.2083 8.27277 9.75153 9.01203 9.01227C9.75129 8.27301 10.208 7.25258 10.208 6.12492C10.208 3.86976 8.37984 2.04159 6.12467 2.04159ZM1.45801 6.12492C1.45801 3.54759 3.54735 1.45825 6.12467 1.45825C8.702 1.45825 10.7913 3.54759 10.7913 6.12492C10.7913 7.30852 10.3504 8.38959 9.62433 9.21209L12.4559 12.0437C12.5698 12.1576 12.5698 12.3423 12.4559 12.4562C12.342 12.5701 12.1573 12.5701 12.0434 12.4562L9.21185 9.62457C8.38935 10.3506 7.30827 10.7916 6.12467 10.7916C3.54735 10.7916 1.45801 8.70225 1.45801 6.12492Z"
                      fill="currentColor"
                    />
                  </svg>
                </Box>
              </InputAdornment>
            ),
          },
        }}
        sx={{
          my:1,
          '& .MuiOutlinedInput-root': {
            backgroundColor: theme.palette.cx.bg.elevated,
            color: theme.palette.cx.light[100],
            fontSize: '12px',
            height: '34px',
          },
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.palette.cx.border.default,
          },
          '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.palette.primary.main,
          },
          '& .MuiInputBase-input::placeholder': {
            color: theme.palette.cx.text.secondary,
            opacity: 1,
          },
        }}
      />
      {/* Team Lists - Side by Side */}
      <Box sx={{ display: 'flex', flex: 1 }}>
        {filteredTeams?.map((team: ProcessedTeam, index: number) => (
          <Stack direction="column" key={team.id} sx={{ flex: 1 }}>
            <Box sx={{ mb: 1, display: 'flex', alignItems: 'left' }}>
              <Typography
                sx={{
                  color: theme.palette.cx.text.secondary,
                  fontWeight: 400,
                  fontSize: '12px',
                  lineHeight: '16px',
                  mb: 0.5,
                }}
              >
                {team.displayName}
              </Typography>
            </Box>
            <TeamItem
              key={team.id}
              team={team}
              index={index}
              trackEntity={selectPlayer}
              singleCurrentlyTrackedObjectId={selectedPlayerId}
            />
          </Stack>
        ))}
      </Box>
    </Stack>
  );
};

export default TrackedObjectSelector;
